import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const PLAYBACK_EASE_DURATION = 400;

/**
 * A full-bleed strip of bottom-aligned photos that scrolls to the left forever.
 *
 * The strip is filled by repeating the authored set of photos until it is wider
 * than the viewport, then translating the track by exactly one set's width. Speed
 * is authored in pixels per second, so adding photos changes how long a lap takes
 * rather than how fast the images move.
 *
 * Clones are inert: their popovers, ids, refs and anchor names are stripped so the
 * originals stay the only anchor targets on the page.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The translating track.
 * @property {HTMLElement} set - The authored set of photos.
 *
 * @extends Component<Refs>
 */
class PhotoStripComponent extends Component {
  requiredRefs = ['track', 'set'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {{ cancel: () => void, current: number } | null} */
  #playbackAnimation = null;

  connectedCallback() {
    super.connectedCallback();

    this.#measureViewport();
    this.#update();

    this.#resizeObserver = new ResizeObserver(this.#onSetResize);
    this.#resizeObserver.observe(this.refs.set);

    window.addEventListener('resize', this.#onWindowResize);

    if (this.dataset.pauseOnHover === 'true') {
      this.addEventListener('pointerenter', this.#pause);
      this.addEventListener('pointerleave', this.#resume);
      this.addEventListener('focusin', this.#pause);
      this.addEventListener('focusout', this.#resume);
    }

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.#pause);
      document.addEventListener('shopify:block:deselect', this.#resume);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    window.removeEventListener('resize', this.#onWindowResize);
    this.removeEventListener('pointerenter', this.#pause);
    this.removeEventListener('pointerleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);
    document.removeEventListener('shopify:block:select', this.#pause);
    document.removeEventListener('shopify:block:deselect', this.#resume);
  }

  #onSetResize = debounce(() => this.#update(), 100);

  #onWindowResize = debounce(() => {
    this.#measureViewport();
    this.#update();
  }, 200);

  /**
   * Writes the scrollbar-free viewport width so the strip can break out of the
   * section's content column without pushing the page into horizontal scroll.
   */
  #measureViewport() {
    this.style.setProperty('--photo-strip-vw', `${document.documentElement.clientWidth}px`);
  }

  #update() {
    const { track, set } = this.refs;
    const setWidth = set.getBoundingClientRect().width;

    // No photos yet, or the block is hidden — leave the animation off.
    if (setWidth === 0) {
      this.setAttribute('data-disabled', '');
      return;
    }

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = setWidth + gap;
    const viewportWidth = this.clientWidth || document.documentElement.clientWidth;

    // One set to scroll away, plus enough sets to keep the viewport covered.
    this.#syncCopies(Math.max(2, Math.ceil(viewportWidth / distance) + 1));

    const speed = Number(this.dataset.speed) || 40;

    this.removeAttribute('data-disabled');
    this.style.setProperty('--photo-strip-distance', `${distance}px`);
    this.style.setProperty('--photo-strip-duration', `${distance / speed}s`);

    this.#restartAnimation();
  }

  /**
   * @param {number} total - Total number of sets the track should contain.
   */
  #syncCopies(total) {
    const { track, set } = this.refs;
    const clones = track.querySelectorAll('[data-photo-strip-clone]');
    const needed = total - 1;

    if (clones.length === needed) return;

    for (const clone of clones) clone.remove();
    for (let i = 0; i < needed; i++) track.appendChild(this.#createInertClone(set));
  }

  /**
   * Deep-clones the authored set and neuters everything that must stay unique to
   * the original: popovers, ids, component refs and CSS anchor names.
   *
   * @param {HTMLElement} set
   * @returns {HTMLElement}
   */
  #createInertClone(set) {
    const clone = /** @type {HTMLElement} */ (set.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-photo-strip-clone', '');
    clone.removeAttribute('ref');
    clone.removeAttribute('data-ref');

    for (const popover of clone.querySelectorAll('[popover]')) popover.remove();

    // Replace the popover custom elements with plain divs so they never upgrade
    // and never fight the originals over refs.
    for (const host of clone.querySelectorAll('anchored-popover-component')) {
      const replacement = document.createElement('div');
      replacement.className = host.className;
      replacement.append(...host.childNodes);
      host.replaceWith(replacement);
    }

    for (const element of clone.querySelectorAll('[ref], [data-ref]')) {
      element.removeAttribute('ref');
      element.removeAttribute('data-ref');
    }

    for (const element of clone.querySelectorAll('[id]')) element.removeAttribute('id');

    // Keep the theme editor from offering a clone as a selectable block.
    for (const element of clone.querySelectorAll('[data-shopify-editor-block]')) {
      element.removeAttribute('data-shopify-editor-block');
    }

    for (const element of clone.querySelectorAll('[style*="anchor-name"]')) {
      /** @type {HTMLElement} */ (element).style.removeProperty('anchor-name');
    }

    return clone;
  }

  #restartAnimation() {
    for (const animation of this.refs.track.getAnimations()) {
      animation.currentTime = 0;
    }
  }

  #pause = () => this.#easePlaybackRate(0);
  #resume = () => this.#easePlaybackRate(1);

  /**
   * Eases the CSS animation's playback rate instead of hard-stopping it, so the
   * strip settles rather than snapping when the pointer arrives.
   *
   * @param {number} to
   */
  #easePlaybackRate(to) {
    const [animation] = this.refs.track.getAnimations();

    if (!animation) return;
    if (this.#playbackAnimation == null && animation.playbackRate === to) return;

    const from = this.#playbackAnimation?.current ?? animation.playbackRate;
    this.#playbackAnimation?.cancel();

    this.#playbackAnimation = animateValue({
      from,
      to,
      duration: PLAYBACK_EASE_DURATION,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#playbackAnimation = null;
      },
    });
  }
}

/**
 * Animate a numeric value over time.
 *
 * @param {Object} params
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration in milliseconds.
 * @param {function(number): void} params.onUpdate - Called on each frame.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - Called when the animation finishes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /** @param {number} currentTime */
  function animate(currentTime) {
    if (cancelled) return;

    const progress = Math.min((currentTime - startTime) / duration, 1);
    currentValue = from + (to - from) * easing(progress);

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      onComplete?.();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('photo-strip-component')) {
  customElements.define('photo-strip-component', PhotoStripComponent);
}
